import type { ClientMsg, ServerMsg } from '../shared/protocol.js';
import { PING_INTERVAL_MS } from '../shared/rules.js';

type Listener = (msg: ServerMsg) => void;
type StatusListener = (connected: boolean) => void;

const RESUME_KEY = 'dice-resume-token';

export class WSClient {
  private ws: WebSocket | null = null;
  private url: string;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private reconnectDelay = 1000;
  private name: string;
  private intentionalClose = false;
  private pingTimer: number | null = null;

  constructor(name: string) {
    this.name = name;
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    this.url = `${proto}//${location.host}/ws`;
  }

  connect(): void {
    this.intentionalClose = false;
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.addEventListener('open', () => {
      this.reconnectDelay = 1000;
      this.send({
        type: 'hello',
        name: this.name,
        resumeToken: localStorage.getItem(RESUME_KEY),
      });
      this.startPing();
      this.emitStatus(true);
    });
    ws.addEventListener('message', (ev) => {
      let msg: ServerMsg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type === 'welcome' && msg.resumeToken) {
        localStorage.setItem(RESUME_KEY, msg.resumeToken);
      }
      for (const l of this.listeners) l(msg);
    });
    ws.addEventListener('close', () => {
      this.stopPing();
      this.emitStatus(false);
      if (this.intentionalClose) return;
      window.setTimeout(() => this.connect(), this.reconnectDelay);
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 15000);
    });
    ws.addEventListener('error', () => { /* close handler reconnects */ });
  }

  setName(name: string): void { this.name = name; }

  /** True, sobald der Socket offen ist (≠ „welcome erhalten"). */
  isConnected(): boolean { return this.ws?.readyState === WebSocket.OPEN; }

  send(msg: ClientMsg): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private emitStatus(connected: boolean): void {
    for (const l of this.statusListeners) l(connected);
  }

  close(): void {
    this.intentionalClose = true;
    this.stopPing();
    this.ws?.close();
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => this.send({ type: 'ping' }), PING_INTERVAL_MS);
  }
  private stopPing(): void {
    if (this.pingTimer != null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }
}

export function clearResumeToken(): void {
  localStorage.removeItem(RESUME_KEY);
}
